import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {CompanyDetail} from '../modules/apps/user-management/users-list/company-detail'
import Products from '../modules/product-master'
import Transactions from '../modules/transactions'
import {Settings} from '../modules/settings/'
import {ProfitAndLoss} from '../modules/profitandloss'
import {BalanceSheet} from '../modules/balancesheet'
import {Register} from '../modules/register'
import UploadRecords from '../modules/uploadeRecords'

import {PageTitle} from '../../_metronic/layout/core'
import ProductCatalogues from '../modules/product-catalogue'
import {WarehouseManagement} from '../modules/warehouse-management'
import {ReturnsManagement} from '../modules/returns-management'
import {CostCenterManagement} from '../modules/cost-center-management'
import {ProfitCenterManagement} from '../modules/profit-center-management'
import {ExpenseManagement} from '../modules/expense-management'
import StockManagement from '../modules/stock-management'
import {StockLevels} from '../modules/stock-levels-report'
import {StockValuation} from '../modules/stock-valuation-report'
import {InventoryAgeing} from '../modules/inventory-ageing-report'
import {ReturnScrap} from '../modules/return-scrap'
import {MovementReport} from '../modules/movement-report'
import {ProfitabilityReport} from '../modules/profitability-report'

const PrivateRoutes = () => {
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
  const InventoryPage = lazy(() => import('../modules/inventory/InventoryPage'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />

        <Route
          path='companies/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        <Route
          path='company/:id'
          element={
            <SuspensedView>
              <CompanyDetail />
            </SuspensedView>
          }
        />

        <Route
          path='products/*'
          element={
            <SuspensedView>
              <Products />
            </SuspensedView>
          }
        />

        <Route
          path='transactions/*'
          element={
            <SuspensedView>
              <Transactions />
            </SuspensedView>
          }
        />

        <Route
          path='settings/'
          element={
            <SuspensedView>
              <Settings />
            </SuspensedView>
          }
        />
        <Route
          path='profitandloss/'
          element={
            <SuspensedView>
              <PageTitle>Profit And Loss Report</PageTitle>
              <ProfitAndLoss />
            </SuspensedView>
          }
        />
        <Route
          path='balancesheet/'
          element={
            <SuspensedView>
              <BalanceSheet />
            </SuspensedView>
          }
        />
        <Route
          path='uploadRecords/*'
          element={
            <SuspensedView>
              <UploadRecords />
            </SuspensedView>
          }
        />
        <Route
          path='register/*'
          element={
            <SuspensedView>
              <PageTitle>Register</PageTitle>
              <Register />
            </SuspensedView>
          }
        />

        {/* Inventory Management Module */}

        <Route
          path='inventory/*'
          element={
            <SuspensedView>
              <InventoryPage />
            </SuspensedView>
          }
        />

        <Route
          path='warehouse-management/*'
          element={
            <SuspensedView>
              <PageTitle>Warehouse Management</PageTitle>
              <WarehouseManagement />
            </SuspensedView>
          }
        />
        <Route
          path='product-catalogue/*'
          element={
            <SuspensedView>
              <ProductCatalogues />
            </SuspensedView>
          }
        />
        <Route
          path='stock/*'
          element={
            <SuspensedView>
              <StockManagement />
            </SuspensedView>
          }
        />
        <Route
          path='returns-management/*'
          element={
            <SuspensedView>
              <PageTitle>Returns Management</PageTitle>
              <ReturnsManagement />
            </SuspensedView>
          }
        />

        {/* Inventory Reports */}
        <Route
          path='stock-levels/*'
          element={
            <SuspensedView>
              <PageTitle>Stock Levels</PageTitle>
              <StockLevels />
            </SuspensedView>
          }
        />
        <Route
          path='stock-valuation/*'
          element={
            <SuspensedView>
              <PageTitle>Stock Valuation</PageTitle>
              <StockValuation />
            </SuspensedView>
          }
        />
        <Route
          path='inventory-ageing/*'
          element={
            <SuspensedView>
              <PageTitle>Inventory Ageing</PageTitle>
              <InventoryAgeing />
            </SuspensedView>
          }
        />
        <Route
          path='return-scrap/*'
          element={
            <SuspensedView>
              <PageTitle>Return Scrap</PageTitle>
              <ReturnScrap />
            </SuspensedView>
          }
        />
        <Route
          path='movement-report/*'
          element={
            <SuspensedView>
              <PageTitle>Movement Report</PageTitle>
              <MovementReport />
            </SuspensedView>
          }
        />
        <Route
          path='profitability-report/*'
          element={
            <SuspensedView>
              <PageTitle>Profitability</PageTitle>
              <ProfitabilityReport />
            </SuspensedView>
          }
        />

        {/* Cost / Profit Center Module */}
        <Route
          path='cost-center-management/*'
          element={
            <SuspensedView>
              <PageTitle>Cost Center Management</PageTitle>
              <CostCenterManagement />
            </SuspensedView>
          }
        />
        <Route
          path='profit-center-management/*'
          element={
            <SuspensedView>
              <PageTitle>Profit Center Management</PageTitle>
              <ProfitCenterManagement />
            </SuspensedView>
          }
        />
        <Route
          path='expense-management/*'
          element={
            <SuspensedView>
              <PageTitle>Expense Management</PageTitle>
              <ExpenseManagement />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
